import type { BookingEntry } from "./schemas/booking.ts";
import type { OwnerOpenSpot } from "./schemas/home.ts";
import { DEFAULT_TIMEZONE, formatSessionTime } from "./time.ts";

/**
 * Open spots to fill (Studio Home H2): the owner sends one WhatsApp message with the free places of
 * the next 48 hours, each time with a link to book it.
 */

/** The window of the open spots card, in hours from now. */
export const OPEN_SPOTS_WINDOW_HOURS = 48;

/**
 * The Desk path that opens the Book page of one session for a member: the business to switch to,
 * and where the member started (`entry`, for `booking_created`). The server puts it on its public
 * Desk address (`DESK_PUBLIC_URL`).
 */
export function memberBookPath(sessionId: string, input: { businessId: string; entry: BookingEntry }): string {
  const query = new URLSearchParams({ business: input.businessId, entry: input.entry });
  return `/member/book/${encodeURIComponent(sessionId)}?${query.toString()}`;
}

/**
 * The message a member reads, in ASD-STE100 Simplified Technical English: one line for each
 * session, with its day and time in the business time zone and its Book link.
 *
 *   Studio Nine Pilates has open places in the next 48 hours.
 *
 *   Wed 21 Oct at 18:00, Reformer Flow: 2 places
 *   https://desk.teamna.app/member/book/...
 *
 *   Open the link to book your place.
 */
export function openSpotsMessage(input: {
  businessName: string;
  timezone?: string | undefined;
  sessions: ReadonlyArray<Pick<OwnerOpenSpot, "groupName" | "startsAt" | "open" | "bookUrl">>;
}): string {
  const timezone = input.timezone ?? DEFAULT_TIMEZONE;
  const lines = input.sessions.map(
    (session) =>
      `${formatSessionTime(new Date(session.startsAt), timezone)}, ${session.groupName}: ${session.open} ${session.open === 1 ? "place" : "places"}\n${session.bookUrl}`,
  );
  return [`${input.businessName} has open places in the next ${OPEN_SPOTS_WINDOW_HOURS} hours.`, ...lines, "Open the link to book your place."].join("\n\n");
}
